/**
 * Pure compliance math behind the SDMS Result Validation → Trend tab.
 *
 * Kept OUT of `SdmsTrendTab.tsx` deliberately: these are the numbers a reviewer
 * signs a validation decision against (LRAA, % of MCL, Watch/Exceeds), so they
 * are unit-tested in isolation rather than only exercised through a chart.
 * Nothing here touches React, Recharts, or the network.
 *
 * See `trendMath.test.ts` for the worked examples that pin each rule.
 */

/** One approved historical result — a single point on the Trend chart. */
export interface TrendPointVm {
  site: string;
  analyte: string;
  /** 'yyyy-MM' — bucketed server-side so timezone can never shift a point. */
  month: string;
  /** Collect timestamp (ISO) — orders results within a month so "latest" is the
   *  most-recent RESULT and LRAA is the last-four RESULTS. Optional; falls back to
   *  `month` when absent, so callers/tests without it behave as before. */
  collectDate?: string | null;
  value: number;
  units: string;
  mcl: number | null;
}

// ── thresholds (product decision 2026-08-01) ────────────────────────────────
/** ≥ this and < EXCEED_PCT → "Watch".  80% is the conventional operational
 *  trigger level: far enough below the limit to act before a violation. */
export const WATCH_PCT = 80;
/** ≥ this → "Exceeds" — at or over the MCL. */
export const EXCEED_PCT = 100;
/** How many trailing results make up the LRAA.  Four consecutive quarterly
 *  samples is the regulatory shape for TTHM/HAA5; using "last four RESULTS"
 *  keeps the figure correct on a non-quarterly cadence too. */
export const LRAA_WINDOW = 4;

/** Sample Result Trend display cap — show only the newest N points per
 *  (site/project · analyte), the SAME "last 30 is enough" rule QC / Surrogate / IS
 *  already use (owner 2026-10-03). The OLDEST beyond the cap are dropped; same-period
 *  siblings within the newest N are kept, so the 2026-09-30 "plot every approved
 *  result" fix still holds for what's shown. The mean is over the displayed set; the
 *  caption states the full window count so the reviewer knows how many exist. */
export const SAMPLE_TREND_MAX_POINTS = 30;

/** The Sample Result Trend caption's count phrase. When the full approved set
 *  exceeds the display cap it says "Newest N of TOTAL …" (so the reviewer sees both
 *  the window total and that only the newest N are drawn); otherwise it states the
 *  plain total. The 12-month window is fixed for this trend. */
export function sampleTrendCountPhrase(total: number, max: number): string {
  const s = (n: number) => (n === 1 ? '' : 's');
  return total > max
    ? `Newest ${max} of ${total} approved results over the last 12 months`
    : `${total} approved result${s(total)} over the last 12 months`;
}

export type TrendStatus = 'In range' | 'Watch' | 'Exceeds' | '—';

/** Mean of a list, or null when empty (never NaN — a blank cell beats "NaN"). */
export const mean = (xs: number[]): number | null =>
  xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;

/** Status pill from % of MCL.  Null/non-finite → '—' so a missing MCL shows a
 *  neutral pill instead of falsely reading "In range". */
export function statusOf(pct: number | null): TrendStatus {
  if (pct === null || !Number.isFinite(pct)) return '—';
  if (pct >= EXCEED_PCT) return 'Exceeds';
  if (pct >= WATCH_PCT) return 'Watch';
  return 'In range';
}

/** Per (site, analyte) roll-up used by both of the tab's summary tables. */
export interface TrendStat {
  latest: number | null;
  latestMonth: string | null;
  /** Arithmetic mean over the whole window (12 months by default). */
  mean12: number | null;
  /** Locational running annual average — mean of the last LRAA_WINDOW results. */
  lraa: number | null;
  /** True when fewer than LRAA_WINDOW results existed.  The average is still
   *  returned (a 2-point mean beats a blank) but the UI flags it. */
  lraaPartial: boolean;
  mcl: number | null;
  units: string;
  /** LATEST ÷ MCL × 100 — deliberately the latest value, not the LRAA: the
   *  reviewer is validating today's result and this drives the Status pill. */
  pct: number | null;
}

/** Roll a point list (any order) into the tab's derived figures. */
export function rollUp(points: TrendPointVm[]): TrendStat {
  // Order by collect date (fall back to the month bucket when absent) so "latest"
  // is the most-recent RESULT and the LRAA is the last-four RESULTS — the SAME
  // rule the chart uses to plot latest-per-month, so the tooltip and this table
  // agree even when a month holds several samples.
  const sortKey = (p: TrendPointVm) => p.collectDate ?? p.month;
  const ordered = [...points].sort((a, b) => {
    const ka = sortKey(a);
    const kb = sortKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
  const values = ordered.map((p) => p.value);
  const last = ordered.length ? ordered[ordered.length - 1] : undefined;
  const tail = ordered.slice(-LRAA_WINDOW).map((p) => p.value);
  const mcl = ordered.find((p) => p.mcl !== null)?.mcl ?? null;
  const latest = last ? last.value : null;
  return {
    latest,
    latestMonth: last ? last.month : null,
    mean12: mean(values),
    lraa: mean(tail),
    lraaPartial: tail.length > 0 && tail.length < LRAA_WINDOW,
    mcl,
    units: last?.units ?? ordered[0]?.units ?? '',
    pct: latest !== null && mcl ? (latest / mcl) * 100 : null,
  };
}

/** Normalise a value to "% of its own MCL" — the shared axis that makes
 *  analytes with different units and limits comparable on one chart.
 *  Returns null when the analyte is unregulated (no MCL to divide by). */
export function pctOfMcl(value: number, mcl: number | null): number | null {
  return mcl ? (value / mcl) * 100 : null;
}

// ── month-label helpers ─────────────────────────────────────────────────────
// Constant lookups, NOT locale formatting — C2 bans toLocale* outside
// utils/datetime.ts, and these labels are chart furniture, not data values.
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'yyyy-MM' → 'Aug'. */
export function monthShort(m: string): string {
  return MON[Number(m.slice(5, 7)) - 1] ?? m;
}
/** 'yyyy-MM' → 'Aug 2025'. */
export function monthLong(m: string): string {
  return `${monthShort(m)} ${m.slice(0, 4)}`;
}
/** 'yyyy-MM' → '07/2026' — the "Latest (07/2026)" column heading. */
export function monthSlash(m: string): string {
  return `${m.slice(5, 7)}/${m.slice(0, 4)}`;
}

/** A Date → its 'yyyy-MM' bucket key (LOCAL month).  The one place the Trend
 *  window needs "now" — kept as a PURE function of its argument (it does not call
 *  `new Date()` itself), so tests pass a fixed date and callers pass `new Date()`
 *  for today.  Used to anchor the x-axis on the current month (client 2026-08-03:
 *  count the 12-month window back from today's date). */
export function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The `count` consecutive month buckets ENDING at `endMonth`, oldest first —
 * e.g. ('2026-07', 12) → ['2025-08' … '2026-07'].
 *
 * The x-axis is built from this rather than from the months that happen to have
 * data, so the chart always shows the WHOLE rolling window ("Sample month
 * (Aug 2025 – Jul 2026)") and a sparse site reads as a gap in a 12-month record
 * instead of silently rescaling the axis to whatever few months exist. Without
 * it, four months of data drew a four-month axis and the reviewer had no visual
 * cue that eight months were missing.
 *
 * Pure string/number arithmetic — no Date, so no timezone or locale can shift a
 * bucket across a month boundary.
 */
export function monthSequence(endMonth: string, count: number): string[] {
  const year = Number(endMonth.slice(0, 4));
  const month = Number(endMonth.slice(5, 7));
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return [];
  const out: string[] = [];
  for (let back = count - 1; back >= 0; back--) {
    let m = month - back;
    let y = year;
    while (m <= 0) {
      m += 12;
      y -= 1;
    }
    out.push(`${y}-${String(m).padStart(2, '0')}`);
  }
  return out;
}

/** Round steps the Y axis is allowed to use, x any power of ten.  1.5 is what
 *  produces the reference design's 15 / 30 / 45 … ladder at the x10 scale. */
const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5];

/**
 * How far above the data the MCL may STRETCH the Y axis (line drawn at its true
 * height) before we instead keep the axis scaled to the DATA and PIN the MCL
 * line to the top edge. 2.5× keeps a limit the series is approaching at true
 * scale (TTHM 78 vs 80); a far-away limit (Fluoride 0.43 vs 2) is pinned so the
 * trend lines stay spread out and readable. The line is ALWAYS drawn either way.
 * (Client 2026-08-07.)
 */
const MAX_MCL_STRETCH = 2.5;
/** Divisions we aim for between 0 and the axis max. */
const TARGET_DIVISIONS = 6;

const tidy = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * Y-axis maximum AND its explicit tick ladder, chosen so the axis divides into
 * round steps — e.g. a series peaking at 78 against an 80 MCL gives
 * `{ max: 90, ticks: [0,15,30,45,60,75,90] }`, exactly the reference design.
 *
 * Two problems this solves at once:
 *  1. Recharts' default domain is [0, dataMax], so a site comfortably below its
 *     limit pushed the MCL reference line OUTSIDE the domain and it silently
 *     did not render — losing the most important mark on the chart precisely
 *     when the reviewer wants to see the headroom.  Callers pass a max that
 *     already accounts for the MCL, and the returned `max` always covers it.
 *  2. Left to pick its own ticks, Recharts labelled in 10s regardless of range.
 *     Supplying `ticks` makes the ladder deterministic and round.
 *
 * Picks the step whose division count is closest to TARGET_DIVISIONS, breaking
 * ties toward the tighter axis and then toward fewer labels.
 */
export function axisTicks(maxValue: number): { max: number; ticks: number[] } {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return { max: 1, ticks: [0, 1] };

  const exp = Math.floor(Math.log10(maxValue / TARGET_DIVISIONS));
  let best: { step: number; divisions: number; max: number; score: number } | null = null;

  for (const e of [exp - 1, exp, exp + 1, exp + 2]) {
    for (const n of NICE_STEPS) {
      const step = tidy(n * 10 ** e);
      if (step <= 0) continue;
      const divisions = Math.ceil(tidy(maxValue / step));
      // Fewer than 3 gridlines reads as no scale; more than 10 is clutter.
      if (divisions < 3 || divisions > 10) continue;
      const score = Math.abs(divisions - TARGET_DIVISIONS);
      const max = tidy(step * divisions);
      if (
        !best ||
        score < best.score ||
        (score === best.score && max < best.max) ||
        (score === best.score && max === best.max && divisions < best.divisions)
      ) {
        best = { step, divisions, max, score };
      }
    }
  }

  // Nothing scored (pathological input) — fall back to a single decade.
  if (!best) {
    const decade = 10 ** Math.ceil(Math.log10(maxValue));
    return { max: decade, ticks: [0, decade] };
  }

  const ticks: number[] = [];
  for (let i = 0; i <= best.divisions; i++) ticks.push(tidy(best.step * i));
  return { max: best.max, ticks };
}

/**
 * Format a result / limit for display.
 *
 * NOT a fixed 1 decimal.  That was the original rule and it mangled trace-level
 * analytes: a Manganese secondary MCL of **0.05 mg/L displayed as "0.1"** — off
 * by a factor of two on a compliance screen — and every trace metal result
 * collapsed to "0.0", so Latest / 12-mo mean / LRAA read as zero for a site
 * that was actually reporting 0.018 mg/L.
 *
 * Rule: values >= 1 keep one decimal (so the reference design's 74.1 / 57.0 are
 * unchanged); values below 1 switch to three significant digits with trailing
 * zeros trimmed, which is what trace concentrations need.
 */
/**
 * Choose the Y axis for the native-units chart. The dashed MCL limit line is
 * ALWAYS drawn when the analyte has an MCL (client 2026-08-07) — the only
 * question is WHERE:
 *
 *  • MCL within {@link MAX_MCL_STRETCH}× the data max → stretch the axis to
 *    include it and draw the line at its TRUE height (the reference TTHM case,
 *    78 vs 80). `mclPinned = false`.
 *  • MCL far above the data (e.g. Fluoride 0.1-0.43 vs a 2 mg/L MCL) → keep the
 *    axis scaled to the DATA so the series stay spread out and readable, and PIN
 *    the dashed line to the TOP edge (caller draws it at `max`, labelled
 *    "(above range)"). `mclPinned = true`.
 *
 * So the reviewer always sees the limit referenced without the trend ever being
 * flattened. An analyte with NO MCL has no line (`mclOnScale = false`).
 */
export function chooseAxis(
  dataMax: number,
  mcl: number | null,
  floor = 0,
): { max: number; ticks: number[]; mclOnScale: boolean; mclPinned: boolean } {
  const headroom = 1.12;
  const dataAxis = axisTicks(Math.max(dataMax, floor) * headroom);
  if (mcl === null) return { ...dataAxis, mclOnScale: false, mclPinned: false };
  if (dataMax <= 0 || mcl <= dataMax * MAX_MCL_STRETCH) {
    const withMcl = axisTicks(Math.max(dataMax, mcl, floor) * headroom);
    return { ...withMcl, mclOnScale: true, mclPinned: false };
  }
  return { ...dataAxis, mclOnScale: true, mclPinned: true };
}

export const r1 = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs === 0) return '0';
  if (abs >= 1) return n.toFixed(1);
  // 3 significant digits for sub-unit values: 0.05 → "0.05", 0.018 → "0.018".
  const digits = Math.min(8, 2 - Math.floor(Math.log10(abs)));
  return n.toFixed(digits).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};
/** Like r1 but ALSO drops the trailing ".0" it leaves on values ≥ 1 (80.0 → "80",
 *  2.0 → "2", 250.0 → "250") while keeping genuine decimals (0.2, 0.01, 74.1) and
 *  the null → "—" guard. Used for the MCL figure — an MCL is often a whole number
 *  but can be fractional (Aluminum 0.2 mg/L), so it must be shown at its natural
 *  precision, NEVER force-rounded to an integer (client 2026-08-06). */
export const trimZeros = (n: number | null | undefined): string => r1(n).replace(/\.0$/, '');
/** Whole percent, or an em dash. */
export const r0 = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : `${Math.round(n)}%`;

/** Numeric rounding for a PLOTTED trend point — mirrors r1's display precision so
 *  the chart line and its tooltip match the summary table.  A fixed 1-decimal
 *  round (the old rule) collapsed trace analytes to 0.1 — Turbidity 0.136 and
 *  0.066 both showed as "0.1" on the chart while the table (via r1) correctly read
 *  0.136 / 0.066.  Rule: values >= 1 keep 1 decimal (22.9, 31.4 unchanged); values
 *  below 1 keep 3 significant figures (0.136, 0.066, 0.05). */
export function roundForChart(n: number): number {
  if (!Number.isFinite(n) || n === 0) return 0;
  const abs = Math.abs(n);
  if (abs >= 1) return Math.round(n * 10) / 10;
  const digits = Math.min(8, 2 - Math.floor(Math.log10(abs)));
  const f = 10 ** digits;
  return Math.round(n * f) / f;
}

/**
 * Normalise a QC-trend window anchor (a row's AnalyzedDate) to an ISO date for the
 * control-chart series scope's `to`. The trend router's contract is that
 * `anchorDate` is ISO, but some grids — SDMS Result Validation (SdmsPackageDetail) —
 * hand it a DISPLAY-formatted value (`MM/DD/YYYY HH:mm`), which the series endpoint's
 * `DateTime? To` cannot bind → HTTP 400, so every dated QC row's trend failed (user
 * report 2026-09-30; the anchor was added 2026-09-28). An already-ISO value passes
 * through UNCHANGED (the working path); a parseable display value becomes `yyyy-MM-dd`
 * (date granularity is all `to` needs — ScopeFilter compares
 * `AnalyzedDate < DATEADD(day, 1, @To)`); an unparseable value returns null so the
 * chart still loads instead of 400.
 */
export function anchorToIso(v: string | null | undefined): string | null {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v;          // already ISO — unchanged
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;          // unparseable → no anchor
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
